import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import BRAND from "../brand";
import { ArrowLeft, AlertTriangle, LogOut, TrendingUp, TrendingDown, Minus, CheckCircle2, XCircle } from "lucide-react";
import { KpiCards } from "./analytics/KpiCards";
import { ChartsSection } from "./analytics/ChartsSection";
import { RepTracker } from "./analytics/RepTracker";

export default function AnalyticsPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const { data } = await axios.get(`${API}/analytics/team`);
        setAnalytics(data);
      } catch (e) {
        setError(e.response?.status === 403 ? "Access denied. Manager or admin access required." : "Failed to load analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getScoreColor = (score) => {
    if (score >= 80) return "text-[#14B8A6]";
    if (score >= 60) return "text-[#F59E0B]";
    if (score > 0) return "text-red-500";
    return "text-slate-300";
  };
  const getScoreBg = (score) => {
    if (score >= 80) return "bg-[#14B8A6]";
    if (score >= 60) return "bg-[#F59E0B]";
    if (score > 0) return "bg-red-500";
    return "bg-slate-200";
  };
  const getTrendIcon = (trend) => {
    if (trend === "improving") return <TrendingUp className="w-4 h-4 text-[#14B8A6]" />;
    if (trend === "declining") return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };
  const getClassBadge = (cls) => {
    switch (cls) {
      case "field-ready": return { label: "Field Ready", bg: "bg-[#14B8A6]/10 text-[#14B8A6]", icon: CheckCircle2 };
      case "needs-coaching": return { label: "Needs Coaching", bg: "bg-[#F59E0B]/10 text-[#F59E0B]", icon: AlertTriangle };
      default: return { label: "Not Ready", bg: "bg-red-50 text-red-500", icon: XCircle };
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 border-4 border-slate-200 border-t-[#2563EB] rounded-full animate-spin" />
        <p className="text-slate-500">Loading team analytics...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
      <div className="text-center">
        <AlertTriangle className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-[#0B1F3B] mb-2">{error}</h2>
        <Link to="/dashboard"><Button className="bg-[#0B1F3B] hover:bg-[#0B1F3B]/90">Return to Dashboard</Button></Link>
      </div>
    </div>
  );

  const kpi = analytics?.kpi || {};
  const members = analytics?.team_members || [];
  const dist = analytics?.readiness_distribution || {};
  const skillGaps = analytics?.skill_gaps || [];
  const coachingPriorities = analytics?.coaching_priorities || [];
  const trend = analytics?.readiness_trend || [];
  const scenarioPerf = analytics?.scenario_performance || [];
  const actions = analytics?.suggested_actions || [];
  const totalReps = kpi.total_reps || 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/dashboard"><Button variant="ghost" size="icon" className="text-slate-500 hover:text-[#0B1F3B]"><ArrowLeft className="w-5 h-5" /></Button></Link>
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-7 w-auto" onError={(e) => { e.target.style.display = 'none'; }} />
              <div>
                <h1 className="text-lg font-bold text-[#0B1F3B]">Team Readiness Overview</h1>
                <p className="text-xs text-slate-400">{BRAND.productName}</p>
              </div>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <Link to="/dashboard" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Dashboard</Link>
              <Link to="/simulator" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Practice</Link>
              <Link to="/history" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">History</Link>
              <Link to="/analytics" className="text-sm font-medium text-[#0B1F3B]">Team</Link>
              {user?.role === "admin" && <Link to="/admin" className="text-sm font-medium text-slate-500 hover:text-[#0B1F3B]">Admin</Link>}
            </nav>
            <div className="flex items-center gap-4">
              <Link to="/profile" className="text-right hidden sm:block hover:opacity-80">
                <p className="text-sm font-medium text-[#0B1F3B]">{user?.name}</p>
                <p className="text-xs text-slate-400 capitalize">{user?.role}</p>
              </Link>
              <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} className="text-slate-500 hover:text-[#0B1F3B]" data-testid="logout-btn">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <KpiCards kpi={kpi} totalReps={totalReps} getScoreColor={getScoreColor} />
        <ChartsSection
          dist={dist} totalReps={totalReps} trend={trend} skillGaps={skillGaps}
          scenarioPerf={scenarioPerf} coachingPriorities={coachingPriorities}
          actions={actions} getScoreColor={getScoreColor} getScoreBg={getScoreBg}
        />
        <RepTracker
          members={members} getScoreColor={getScoreColor} getScoreBg={getScoreBg}
          getClassBadge={getClassBadge} getTrendIcon={getTrendIcon}
        />
      </main>

      <footer className="border-t border-slate-200/60 bg-white mt-4">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={BRAND.logoUrl} alt="Revyniqx" className="h-6 w-auto opacity-60" onError={(e) => { e.target.style.display = 'none'; }} />
              <span className="text-sm text-slate-400">{BRAND.productName}</span>
            </div>
            <div className="text-center sm:text-right">
              <p className="text-xs text-slate-400">{BRAND.poweredBy}</p>
              <p className="text-xs text-slate-300">{BRAND.companyAttribution}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
